import React, {MouseEventHandler, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router-dom'
import Checkbox from '@material-ui/core/Checkbox'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import {
  analysisHrvSummaryApi,
  checkAvailabilityHrvSummaryAnalysisApi,
  SearchedHRV,
} from 'api/analysisApi'
import ActionButton from 'components/atoms/Buttons/ActionButton'
import TableButton from 'components/atoms/Buttons/TableButton'
import {ClientName} from 'components/atoms/ClientName'
import Pagination from 'components/molcules/Pagination'
import TableCell from 'components/Table/TableCell'
import TableReportSearch from 'components/Table/TableReportSearch'
import useStyles from 'components/Table/useTableStyles'
import useToolbarStyles from 'components/Table/useTableToolbarStyles'
import {AnalysisReportType} from 'constants/AnalysisConstant'
import {getHrvIndividualReportPath} from 'constants/RouteConstant'
import useHrv from 'features/analysis/useHrv'
import useFailureModal from 'features/modal/useFailureModal'
import useNoticeConfirm from 'features/modal/useNoticeConfirm'
import {statusToI18nString} from 'helpers/analysisHelper'
import {
  dateToPeriodString,
  getDefaultPeriodDate,
  isoStringToDateAndTimeString,
} from 'helpers/dateHelper'
import {getQueryFromUrl} from 'helpers/commonHelper'
import openInNewTab from 'helpers/openInNewTab'
import useSuccessModal from 'features/modal/useSuccessModal'
import {useAnalysisConfirm} from 'features/analysis/useAnalysisConfirm'

interface TableToolBarProps {
  selectedId: number
  isShowBasicAnalysisButton: boolean
  isShowAdvancedAnalysisButton: boolean
}

function TableToolbar({
  selectedId,
  isShowBasicAnalysisButton,
  isShowAdvancedAnalysisButton,
}: TableToolBarProps) {
  const location = useLocation()
  const {t} = useTranslation()
  const classes = useToolbarStyles()
  const {query, search, onSearch} = useHrv()
  const {onOpen: onFailureModalOpen} = useFailureModal()
  const {onOpen: onSuccessModalOpen} = useSuccessModal()
  const {onOpen: onNoticeDialogOpen} = useNoticeConfirm()
  const {onCancel: onAnalysisCancel} = useAnalysisConfirm()

  const [selection] = useState<PeriodDate>(getDefaultPeriodDate())

  const summaryRequest = async (reportType: ReportType) => {
    try {
      const checkAvailabilityRes = await checkAvailabilityHrvSummaryAnalysisApi(
        {
          hrvId: selectedId,
        },
      )
      if (!checkAvailabilityRes.data.available) {
        onFailureModalOpen(String(checkAvailabilityRes.data.message))
        return
      }
      await analysisHrvSummaryApi({
        hrvId: selectedId,
        reportType,
      })
      onSuccessModalOpen(t('IProcessSuccess'))
      onSearch(query)
    } catch (error) {
      onAnalysisCancel()
      onFailureModalOpen(String(error.message))
    }
  }

  const handleSummaryClick = async (reportType: ReportType) => {
    if (selectedId === 0) {
      onFailureModalOpen(t('ISelectRequired'))
      return
    }
    onNoticeDialogOpen({
      title: t('INoticeTitle'),
      message: t('INoticeConfirmDesc'),
    }).then((result) => {
      if (result.payload) summaryRequest(reportType)
    })
  }

  /** Set default value for search query if it is stored in url */
  useEffect(() => {
    const initQuery = getQueryFromUrl(location.search)
    onSearch({
      ...query,
      search: {searchName: 'NAME', searchValue: ''},
      period: dateToPeriodString(selection),
      paging: {
        page: 0,
        size: 10,
      },
      ...initQuery,
    })
  }, [])

  return (
    <div className={classes.root}>
      <TableReportSearch
        reportType={AnalysisReportType.HRVIndividual}
        isRoot={false}
        query={query}
        search={search}
        onSearch={onSearch}
      />

      <div className={classes.containerOptional}>
        {isShowBasicAnalysisButton && (
          <ActionButton onClick={() => handleSummaryClick('P1')}>
            {t('IBasicSummary')}
          </ActionButton>
        )}
        {isShowAdvancedAnalysisButton && (
          <ActionButton onClick={() => handleSummaryClick('P2')}>
            {t('IPremiumSummary')}
          </ActionButton>
        )}
      </div>
    </div>
  )
}

interface RenderTableRowProps {
  item: SearchedHRV
  onClick: (item: SearchedHRV) => void
  isSelected: (item: SearchedHRV) => boolean
}

function RenderTableRow({item, onClick, isSelected}: RenderTableRowProps) {
  const {t} = useTranslation()

  const handleResultClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    const clientName = t('IGetFullName', {
      firstName: item.firstName ?? '',
      lastName: item.lastName ?? '',
    }).trim()
    openInNewTab(getHrvIndividualReportPath(item.id, clientName))
  }

  const labelId = `enhanced-table-checkbox-${item.id}`

  const checked = isSelected(item)

  return (
    <TableRow hover onClick={() => onClick(item)}>
      <TableCell padding='checkbox'>
        <Checkbox checked={checked} inputProps={{'aria-labelledby': labelId}} />
      </TableCell>
      <TableCell align='center'>{item.id}</TableCell>
      <TableCell align='center'>
        <ClientName
          firstName={item.firstName}
          lastName={item.lastName}
          birth={item.birth}
          gender={item.gender}
        />
      </TableCell>
      <TableCell align='center'>{item.fileName}</TableCell>
      <TableCell align='center'>
        {isoStringToDateAndTimeString(String(item.createdAt))}
      </TableCell>
      <TableCell align='center'>{item.normdbVersion || '-'}</TableCell>
      <TableCell align='center'>
        {t(statusToI18nString(item.progress))}
      </TableCell>
      <TableCell align='center'>
        <TableButton
          onClick={handleResultClick}
          disabled={item.progress !== 'SUCCESS'}
        >
          {t('IRead')}
        </TableButton>
      </TableCell>
    </TableRow>
  )
}

function PageTable({
  isShowBasicAnalysisButton,
  isShowAdvancedAnalysisButton,
}: {
  isShowBasicAnalysisButton: boolean
  isShowAdvancedAnalysisButton: boolean
}) {
  const {t} = useTranslation()
  const classes = useStyles()

  const {loading, query, paging, pagingInfo, items, onSearch} = useHrv()
  const [selectedId, setSelectedId] = useState<number>(0)

  const setPageIndex = (page: number) => {
    onSearch({
      ...query,
      paging: {
        page,
        size: paging.size,
      },
    })
  }

  const onSizeChanged = (
    event: React.ChangeEvent<{name?: string; value: unknown}>,
  ) => {
    const size = parseInt(event.target.value as string, 10)
    onSearch({
      ...query,
      paging: {
        page: 0,
        size,
      },
    })
  }

  const handleRowClick = (item: SearchedHRV) => {
    setSelectedId(item.id)
  }

  const isRowSelected = (item: SearchedHRV) => selectedId === item.id

  const emptyRows = items === null ? 5 : 5 - items.length

  return (
    <TableContainer className={classes.tableContainer}>
      <TableToolbar
        selectedId={selectedId}
        isShowBasicAnalysisButton={isShowBasicAnalysisButton}
        isShowAdvancedAnalysisButton={isShowAdvancedAnalysisButton}
      />
      <Table className={classes.table}>
        <TableHead>
          <TableRow className={classes.tableHeader}>
            <TableCell align='center' padding='none' />
            <TableCell align='center' padding='none'>
              {t('IEEGIndividualIndex')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IPatientName')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IFileName')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IMeasureDate')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IVersion')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IProgress')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IAnalysisReport')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <RenderTableRow
              key={item.id}
              item={item}
              onClick={handleRowClick}
              isSelected={isRowSelected}
            />
          ))}
          {emptyRows > 0 && (
            <TableRow
              style={{height: 43 * emptyRows, backgroundColor: '#F9F9FB'}}
            >
              <TableCell colSpan={12} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Pagination
        totalPageCount={pagingInfo.totalPages}
        currentPageIndex={query.paging.page ?? 0}
        itemCountPerPage={query.paging.size ?? 10}
        setCurrentPageIndex={setPageIndex}
        loading={loading}
        onItemCountPerPageChanged={onSizeChanged}
      />
    </TableContainer>
  )
}

export default PageTable
